import { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import ParchmentLayout from '../components/ParchmentLayout';
import OrnamentSeparator from '../components/OrnamentSeparator';
import { TEAM_FACTION_NAMES } from '../components/teamTheme';

// Generatore di nomi fantasy casuali
const FANTASY_NAMES = [
  'Aragorn', 'Gandalf', 'Legolas', 'Gimli', 'Frodo', 'Samwise', 'Merry', 'Pippin',
  'Boromir', 'Faramir', 'Eowyn', 'Arwen', 'Galadriel', 'Elrond', 'Thorin', 'Bilbo',
  'Conan', 'Merlin', 'Lancelot', 'Arthur', 'Percival', 'Galahad', 'Morgana', 'Nimue',
  'Draven', 'Theron', 'Lyra', 'Kael', 'Zephyr', 'Orion', 'Luna', 'Phoenix',
  'Raven', 'Storm', 'Shadow', 'Blaze', 'Frost', 'Ember', 'Sage', 'Jade',
  'Drake', 'Wolf', 'Bear', 'Hawk', 'Fox', 'Lion', 'Tiger', 'Panther',
  'Aldric', 'Branwen', 'Cedric', 'Davian', 'Eira', 'Finnian', 'Gwyneth', 'Hadrian', 'Isolde', 'Jareth', 'Kaida', 'Leif', 'Mira', 'Nolan', 'Oberon', 'Persephone',
  'Quinn', 'Rowan', 'Serat', 'Talon', 'Ulric', 'Vesper', 'Wren', 'Xander',
  'Yara', 'Zane', 'Alaric', 'Briar', 'Caspian', 'Dorian', 'Elara', 'Faelan',
  'Gideon', 'Helena', 'Ithil', 'Jasper', 'Kiera', 'Lucian', 'Morgana',
  'Nerys', 'Orion', 'Phaedra', 'Quillon', 'Rhiannon', 'Sylas', 'Tamsin',
  'Ulysses', 'Vanya', 'Wystan', 'Xenia', 'Yorick', 'Zara', 'Azrael', 'Bellatrix', 'Cyrus', 'Daphne', 'Ezekiel', 'Freya', 'Garrick', 'Hestia', 'Ignatius', 'Juno', 'Kalliope', 'Leander', 'Morrigan', 'Nyx', 'Odin', 'Perseus', 'Ragnar', 'Selene', 'Thanatos', 'Umbra', 'Valkyrie', 'Wraith', 'Xerxes',
  'Yggdrasil', 'Zephyrine'
];

const generateFantasyName = (): string => {
  const randomIndex = Math.floor(Math.random() * FANTASY_NAMES.length);
  const randomSuffix = Math.floor(Math.random() * 999);
  return randomSuffix > 99 ? `${FANTASY_NAMES[randomIndex]}${randomSuffix}` : FANTASY_NAMES[randomIndex];
};

const TEAM_TEXT_CLASSES: Record<string, string> = {
  'blue': 'text-team-blue',
  'red': 'text-team-red',
  'green': 'text-team-green',
  'yellow': 'text-team-yellow',
};

function JoinScreen() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [isJoining, setIsJoining] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [nickname, setNickname] = useState(() => generateFantasyName());

  const sessionId = searchParams.get('session');
  const teamParam = searchParams.get('team');
  const timestamp = searchParams.get('t');
  const signature = searchParams.get('sig');

  // Detect token type
  const isStaticToken = !sessionId && teamParam && signature && !timestamp;
  const isLegacyToken = sessionId && timestamp && signature;
  const isDirectAccess = !signature && !sessionId && !timestamp;

  const [isCheckingSession, setIsCheckingSession] = useState(true);

  useEffect(() => {
    const checkExistingSession = async () => {
      // Validate token format
      if (!isStaticToken && !isLegacyToken && !isDirectAccess) {
        setError('QR code non valido. Scansiona un QR code ufficiale.');
        setIsCheckingSession(false);
        return;
      }

      // Check if user already joined (has audienceId and sessionId in localStorage)
      const storedAudienceId = localStorage.getItem('audienceId');
      const storedSessionId = localStorage.getItem('sessionId');
      const storedTeamColor = localStorage.getItem('teamColor');

      if (storedAudienceId && storedSessionId && storedTeamColor) {
        // Verify with server if audienceId still exists (might have been reset)
        try {
          const response = await fetch(`/api/audience/check/${storedAudienceId}`);
          const data = await response.json();

          if (data.exists) {
            // Sessione attiva, redirect a /audience (mantieni squadra originale)
            navigate('/audience', { replace: true });
            return;
          } else {
            // Reset avvenuto, pulisci localStorage e permetti nuovo join
            console.log('[JoinScreen] Session was reset, clearing localStorage');
            localStorage.removeItem('audienceId');
            localStorage.removeItem('sessionId');
            localStorage.removeItem('teamColor');
            // Continua con normale flusso join
          }
        } catch (error) {
          console.error('[JoinScreen] Error checking audience:', error);
          // In caso di errore di rete, pulisci e permetti nuovo join
          localStorage.removeItem('audienceId');
          localStorage.removeItem('sessionId');
          localStorage.removeItem('teamColor');
        }
      }

      setIsCheckingSession(false);
    };

    checkExistingSession();
  }, [sessionId, timestamp, signature, teamParam, isStaticToken, isLegacyToken, isDirectAccess, navigate]);

  const handleJoin = async () => {
    if (!isStaticToken && !isLegacyToken && !isDirectAccess) {
      setError('QR code non valido.');
      return;
    }

    if (!nickname.trim()) {
      setError('Inserisci un nickname');
      return;
    }

    setIsJoining(true);
    setError(null);

    try {
      const requestBody: any = {
        nickname: nickname.trim(),
      };

      // Build request body based on token type
      if (isDirectAccess) {
        // Direct access: only team (optional)
        if (teamParam) {
          requestBody.team = teamParam;
        }
      } else if (isStaticToken) {
        // Static token: only team + sig
        requestBody.team = teamParam;
        requestBody.sig = signature;
      } else if (isLegacyToken) {
        // Legacy token: session + t + sig + optional team
        requestBody.session = sessionId;
        requestBody.t = timestamp;
        requestBody.sig = signature;
        if (teamParam) {
          requestBody.team = teamParam;
        }
      }

      const response = await fetch('/api/audience/join', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(requestBody),
      });

      if (!response.ok) {
        const errorData = await response.json();

        // Handle specific errors
        if (errorData.error === 'No active show') {
          throw new Error('Nessuno spettacolo attivo al momento. Riprova quando lo show inizia!');
        }
        if (errorData.error === 'nickname_taken') {
          throw new Error(errorData.reason || 'Questo nickname è già in uso. Scegline un altro!');
        }

        throw new Error(errorData.error || 'Impossibile unirsi alla sessione');
      }

      const data = await response.json();

      // Save to localStorage
      localStorage.setItem('audienceId', data.audienceId);
      localStorage.setItem('sessionId', data.sessionId);
      localStorage.setItem('teamColor', data.teamColor);
      localStorage.setItem('nickname', nickname.trim());

      // Verifica che localStorage sia stato scritto correttamente
      const savedSessionId = localStorage.getItem('sessionId');
      const savedTeamColor = localStorage.getItem('teamColor');
      console.log('[JoinScreen] Saved to localStorage:', { savedSessionId, savedTeamColor });

      if (!savedSessionId || !savedTeamColor) {
        throw new Error('Errore salvataggio dati sessione. Riprova.');
      }

      // Navigate to welcome screen
      navigate('/audience/welcome');
    } catch (err: any) {
      setError(err.message || 'Impossibile unirsi alla sessione. Riprova.');
      setIsJoining(false);
    }
  };

  // Show loading while checking existing session
  if (isCheckingSession) {
    return (
      <ParchmentLayout teamColor={teamParam?.toUpperCase()} className="flex items-center justify-center p-4">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-parchment-400 mx-auto mb-4"></div>
          <p className="text-ink-light text-lg font-heading">Verifica sessione...</p>
        </div>
      </ParchmentLayout>
    );
  }

  const teamTextClass = teamParam ? (TEAM_TEXT_CLASSES[teamParam.toLowerCase()] || 'text-ink') : 'text-ink';
  const factionName = teamParam ? (TEAM_FACTION_NAMES[teamParam.toUpperCase()] || teamParam) : '';

  return (
    <ParchmentLayout teamColor={teamParam?.toUpperCase()} className="flex items-center justify-center p-4">
      <div className="max-w-md w-full">
        <div className="parchment-card p-8">
          <div className="text-center mb-6">
            <img src="/images/dw_logo.png" alt="Dungeon World - The Show" className="mx-auto w-48 mb-1" />
            <p className="text-ink-light font-body italic text-xl">
              Avventura teatrale interattiva
            </p>
          </div>

          <OrnamentSeparator />

          {error && (
            <div className="mb-6 bg-team-red-bg border-2 border-team-red/30 rounded-lg p-4">
              <p className="text-team-red text-base font-body">{error}</p>
            </div>
          )}

          <div className="space-y-4 mb-6">
            {/* Campo Nickname */}
            <div className="parchment-panel">
              <label htmlFor="nickname" className="text-ink-light text-base mb-2 block font-heading">
                Nome di Battaglia
              </label>
              <input
                id="nickname"
                type="text"
                value={nickname}
                onChange={(e) => setNickname(e.target.value)}
                placeholder="Il tuo nome da eroe"
                maxLength={20}
                className="input-parchment"
              />
            </div>

            {/* Squadra assegnata */}
            {teamParam && (
              <div className="parchment-panel text-center">
                <p className="text-ink-light text-base mb-1 font-heading">La tua Compagnia</p>
                <p className={`font-heading font-bold text-2xl ${teamTextClass}`}>
                  {factionName}
                </p>
              </div>
            )}
          </div>

          <OrnamentSeparator />

          <button
            onClick={handleJoin}
            disabled={isJoining || (!isStaticToken && !isLegacyToken && !isDirectAccess) || !nickname.trim()}
            className="w-full btn-fantasy text-xl py-4 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isJoining ? 'Connessione...' : 'Entra nel Portale'}
          </button>
        </div>
      </div>
    </ParchmentLayout>
  );
}

export default JoinScreen;
